import * as PIXI from "pixi.js";
import { Panel, PanelState } from "../panel/Panel";
import { observer } from "src/shared/util/Observer";
import { SpinResponse } from "src/shared/util/Types";
import { BonusCounter } from "../bonus/BonusCounter";
import { BonusIntro } from "../bonus/BonusIntro";
import { BonusWin } from "../bonus/BonusWin";
import { GameAssets } from "src/shared/util/GameAssets";
import { GameEngine } from "../server/GameEngine";
import { UI, UIEvent } from "src/shared/UI/UI";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import AppSingleton from "src/shared/util/AppSingleton";
import { Spine } from "@pixi/spine-pixi";
import { Sprite } from "pixi.js";
import { delay } from "src/shared/util/UtilFunctions";
import gsap from "gsap";

export let backgroundSize: PIXI.PointData = { x: 0.0, y: 0.0 };

export class GameView extends PIXI.Container {
  public static app: PIXI.Application<PIXI.Renderer>;

  private backgroundMain: PIXI.Sprite;
  private backgroundFs: PIXI.Sprite;

  private temple: Sprite;
  private templeFs: Sprite;
  private swans: Sprite;
  private swansFs: Sprite;
  private trees: Sprite;
  private treesFs: Sprite;

  private panel: Panel;
  private UI!: UI;
  private bonusIntro!: BonusIntro;
  private bonusWin!: BonusWin;
  private bonusCounter: BonusCounter;
  private response!: SpinResponse;
  private app: PIXI.Application<PIXI.Renderer>;

  constructor() {
    super();

    this.app = AppSingleton.getInstance().app;
    this.app.stage.on('resize', this.resize, this);

    observer.on('gameActionResponse', this.onSpinResponse.bind(this));
    window.addEventListener('keydown', this.handleKeyDown.bind(this));

    this.backgroundMain = new PIXI.Sprite(GameAssets.textures.mainBg);
    this.backgroundMain.anchor.set(0.5);
    this.backgroundFs = new PIXI.Sprite(GameAssets.textures.fsBg);
    this.backgroundFs.anchor.set(0.5);
    this.backgroundFs.visible = false;

    const cloudsMask = new PIXI.Graphics();

    cloudsMask.rect(
      -this.backgroundMain.width * this.backgroundMain.scale.x / 2,
      -this.backgroundMain.height * this.backgroundMain.scale.y / 2,
      this.backgroundMain.width * this.backgroundMain.scale.x,
      this.backgroundMain.height * this.backgroundMain.scale.y
    ).fill(0xffffff);


    this.temple = new Sprite(GameAssets.textures.gameAtlas.textures["temple.webp"]);
    this.temple.anchor.set(0.5);
    this.temple.zIndex = -2;

    this.templeFs = new Sprite(GameAssets.textures.gameAtlas.textures["temple_fs.webp"]);
    this.templeFs.anchor.set(0.5);
    this.templeFs.zIndex = -2;
    this.templeFs.visible = false;

    this.swans = new Sprite(GameAssets.textures.gameAtlas.textures["swans.webp"]);
    this.swans.anchor.set(0.5);
    this.swans.zIndex = -2;

    this.swansFs = new Sprite(GameAssets.textures.gameAtlas.textures["swans_fs.webp"]);
    this.swansFs.anchor.set(0.5);
    this.swansFs.zIndex = -2;
    this.swansFs.visible = false;

    this.trees = new Sprite(GameAssets.textures.gameAtlas.textures["trees.webp"]);
    this.trees.anchor.set(0.5);
    this.trees.zIndex = -2;

    this.treesFs = new Sprite(GameAssets.textures.gameAtlas.textures["trees_fs.webp"]);
    this.treesFs.anchor.set(0.5);
    this.treesFs.zIndex = -2;
    this.treesFs.visible = false;

    this.panel = new Panel();
    this.panel.on(PanelState.IDLE, this.onPanelIdle, this);
    this.panel.on(PanelState.CASCADE, this.onCascade, this);
    this.panel.on('resetQuickStop', this.onResetQuickStop, this);
    this.panel.init(GameEngine.getInstance().getLoadResponse().panelNew);

    this.bonusCounter = new BonusCounter();
    this.bonusCounter.scale.set(0.7);
    this.bonusCounter.visible = false;

    this.UI = new UI();
    this.UI.on('buy', this.onBuy, this);
    this.UI.on('spin', this.onSpin, this);
    this.UI.on('stop', this.onQuickStop, this);
    this.UI.updateVisualBalance();
    this.UI.setWin(0);
    this.UI.resetSpinButton();

    this.bonusIntro = new BonusIntro();
    this.bonusIntro.position.set(0, 0);
    this.bonusIntro.visible = false;
    this.bonusIntro.on('close', this.onBonusPopupClose, this);

    this.bonusWin = new BonusWin();
    this.bonusWin.position.set(0, 0);
    this.bonusWin.visible = false;
    this.bonusWin.on('close', this.onBonusWinPopupClose, this);

    this.addComponentsOnStage();
  }

  onPortrait(data?: any) {
    const maxRatio = 1;
    const maxScale = 1.8;
    const ratio = data.ratio;

    let scale: number;
    let x = 0;
    let y = -200;
    if (ratio < maxRatio) {
      scale = maxScale - (ratio / maxRatio) * 0.3;
      y = -200 - ((1 - (ratio / maxRatio)) * 50);
      x = 400;
    } else {
      scale = maxScale;
    }
    this.backgroundMain.scale.set(scale);
    this.backgroundMain.position.set(x, y);
    this.backgroundFs.scale.set(scale);
    this.backgroundFs.position.set(x, y);

    this.temple.position.set(-800, -50);
    this.swans.position.set(850, 300);
    this.trees.position.set(-970, 0);

    this.templeFs.position.set(-800, -120);
    this.swansFs.position.set(850, 200);
    this.treesFs.position.set(-1050, -100);

    this.bonusCounter.position.set(0, -850);

    backgroundSize = { x: this.backgroundMain.width, y: this.backgroundMain.height };

    this.panel.onPortrait(data);
  }

  onLandscape(data?: any) {
    this.backgroundMain.scale.set(1, 1);
    this.backgroundMain.position.set(0, 0);
    this.backgroundFs.scale.set(1, 1);
    this.backgroundFs.position.set(0, 0);

    this.temple.position.set(-720, -120);
    this.swans.position.set(850, 200);
    this.trees.position.set(-1050, -100);

    this.templeFs.position.set(-720, -120);
    this.swansFs.position.set(850, 200);
    this.treesFs.position.set(-1050, -100);

    this.bonusCounter.position.set(400, -450);

    backgroundSize = { x: this.backgroundMain.width, y: this.backgroundMain.height };

    this.panel.onLandscape(data);
  }

  addComponentsOnStage() {
    this.addChild(this.backgroundMain);
    this.addChild(this.backgroundFs);
    this.addChild(this.panel);
    this.addChild(this.bonusCounter);
    this.addChild(this.bonusIntro);
    this.addChild(this.bonusWin);
    this.addChild(this.UI);
    // this.addChild(this.cloudsBack);
    // this.addChild(this.temple);
    // this.addChild(this.trees);
    // this.addChild(this.templeFs);
    // this.addChild(this.swansFs);
    // this.addChild(this.treesFs);
  }

  handleKeyDown(event: any) {
    switch (event.code) {
    }
  }

  _closeBonusPopup() {
    this.bonusIntro.close();
  }

  _closeBonusWinPopup() {
    this.bonusWin.close();
  }

  async onPanelIdle() {
    console.log("PANEL IDLE - Current state:", GlobalState.getInstance().state);
    console.log("Response:", this.response);
    if (GlobalState.getInstance().state === GameState.MAIN_TO_BONUS) {
      this.UI.setWin(this.response.roundWin);
      if (this.response.freespins === 5) {
        (async () => {
          await delay(2000);
          this.UI.setWin(0);
        })();
      }
      this.UI.updateVisualBalance();
      if (this.response.freespins) {
        await this.bonusIntro.show(this.response.freespins);
        this.UI.lock();
        this.UI.once(UIEvent.SCREEN_CLICK, this._closeBonusPopup, this);
      }
    } else if (GlobalState.getInstance().state === GameState.BONUS_TO_MAIN) {
      this.UI.setWin(this.response.totalWin);

      this.bonusCounter.visible = false;

      await this.bonusWin.show(this.response.totalWin);

      this.UI.lock();
      this.UI.once(UIEvent.SCREEN_CLICK, this._closeBonusWinPopup, this);
    } else if (GlobalState.getInstance().state === GameState.BONUS) {
      if (this.response.totalWin) {
        this.UI.setWin(this.response.totalWin);
      }
      if (this.response.freespins) {
        this.bonusCounter.setFreespins(this.response.freespins);
      }
      setTimeout(() => {
        this.startSpin();

        this.UI.setStopButton();
        // TODO: This should be done on the backend
        this.bonusCounter.decrement();
        GameEngine.getInstance().gameActionRequest();
      }, 300);
    } else {
      this.UI.setWin(this.response.totalWin);
      this.UI.updateVisualBalance();
      this.UI.resetSpinButton();
    }
  }

  onResetQuickStop() {
    this.UI.setStopButton();
  }

  onCascade() {
    // if (GlobalState.getInstance().state === GameState.BONUS && this.response.totalWin) {
    //   this.UI.setWin(this.response.totalWin);
    // } else {
    //   this.UI.setWin(this.response.roundWin);
    // }
    GameEngine.getInstance().gameActionRequest();
  }

  onBuy() {
    GameEngine.getInstance().buy();
    this.startSpin(true);
    this.UI.setWin(0);
    this.UI.setStopButton();
  }

  onBonusPopupClose() {
    this.bonusCounter.visible = true;
    if (this.response.freespins) {
      this.bonusCounter.setFreespins(this.response.freespins);
    }
    this.backgroundFs.visible = true;
    this.backgroundFs.alpha = 0;
    gsap.to(this.backgroundFs, { duration: 1, alpha: 1 });


    this.temple.visible = false;
    this.swans.visible = false;
    this.trees.visible = false;

    this.templeFs.visible = true;
    this.swansFs.visible = true;
    this.treesFs.visible = true;

    this.panel.setGameState(GameState.BONUS);

    this.onSpin();
  }

  onBonusWinPopupClose() {
    this.UI.resetSpinButton();
    this.bonusCounter.visible = false;

    gsap.to(this.backgroundFs, {
      duration: 1, alpha: 0, onComplete: () => {
        this.backgroundFs.visible = false;
      }
    });

    this.temple.visible = true;
    this.swans.visible = true;
    this.trees.visible = true;

    this.templeFs.visible = false;
    this.swansFs.visible = false;
    this.treesFs.visible = false;

    this.panel.setGameState(GameState.MAIN);
  }

  onSpin() {
    if (GlobalState.getInstance().state !== GameState.BONUS) {
      GameEngine.getInstance().gameActionRequest();
      this.startSpin();
      if (GlobalState.getInstance().state === GameState.MAIN_TO_BONUS) {
        this.bonusCounter.decrement();
      } else {
        this.UI.setWin(0);
      }
    }
  }

  async onSpinResponse(response: SpinResponse) {
    this.response = response;
    // console.log(this.response);
    this.panel.setData(this.response);
    this.UI.updateVisualBalance();
  }

  onQuickStop() {
    console.log('quickStop');
    this.panel.quickStop();
  }

  private resize(data: any) {
    if (data.ratio < 1.4) {
      this.onPortrait(data);
    } else {
      this.onLandscape(data);
    }
  }

  private startSpin(isBuy: boolean = false) {
    if (this.panel.getState() === PanelState.IDLE) {
      // Don't decrease the balance on Freespins start
      if (GlobalState.getInstance().state === GameState.BONUS_TO_MAIN || GlobalState.getInstance().state === GameState.MAIN) {
        this.UI.updateVisualBalance();
      }
      this.panel.spin();
    }
  }
}
